/**
 * LiveClock — small clock that ticks every 30s. Used in the briefing header
 * so the date/time is always the user's local view (not UTC from the API).
 */
import { useEffect, useRef, useState } from 'react';

interface LiveClockProps {
  /** 'time' = "3:47 PM"; 'full' = "Tue · May 19 · 3:47 PM"; 'date' = date only */
  format?: 'time' | 'full' | 'date';
  className?: string;
}

function format(d: Date, kind: LiveClockProps['format']): string {
  const time = d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  const date = d.toLocaleDateString([], { weekday: 'long', month: 'long', day: 'numeric' });
  if (kind === 'time') return time;
  if (kind === 'date') return date;
  return `${date} · ${time}`;
}

export function LiveClock({ format: kind = 'full', className }: LiveClockProps) {
  const [now, setNow] = useState<Date>(() => new Date());
  // Refs hold the two timer ids so cleanup can clear both. Avoids the bug
  // where the previous version tried to attach `_interval` as a property of
  // the setTimeout return value — a number in browsers, throws on assign.
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    // Align ticks to the start of the minute, then tick every 30s after that
    const msUntilNextMinute = 60_000 - (Date.now() % 60_000);
    timeoutRef.current = setTimeout(() => {
      setNow(new Date());
      intervalRef.current = setInterval(() => setNow(new Date()), 30_000);
    }, msUntilNextMinute);
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return <span className={className} title={now.toString()}>{format(now, kind)}</span>;
}

/** Greeting computed from the user's local hour (not server UTC). */
export function localGreeting(): string {
  const h = new Date().getHours();
  if (h < 5) return 'Good evening';
  if (h < 12) return 'Good morning';
  if (h < 17) return 'Good afternoon';
  if (h < 22) return 'Good evening';
  return 'Working late';
}
